/**
 * @file Pure brief-review logic — zero DOM dependency.
 *
 * During Vue migration:
 *   - formatBriefDownload → utils/format.ts
 *   - addFact / removeFact → useBriefStore().addFact() / .removeFact()
 *   - normalizeColors → utils/colors.ts
 */
import type { MarketingBrief, MarketingBriefFacts } from '../types.ts';

// ── BRIEF DOWNLOAD FORMATTING ──────────────────────────────────────

/**
 * Format marketing brief as a plain-text download.
 */
export const formatBriefDownload = (mb: MarketingBrief, existingUrl?: string): string => {
  const lines: string[] = [
    '# Business DNA — ' + (mb.companyName || 'Site'),
    'URL: ' + (mb.sourceUrl || existingUrl || ''),
    'Industry: ' + (mb.industry || ''),
    '',
    '## Overview',
    mb.businessOverview || '',
    '',
    '## Tagline',
    mb.tagline || '',
    '',
    '## Brand Identity',
    'Tone: ' + (mb.toneOfVoice ?? []).join(', '),
    'Values: ' + (mb.brandValues ?? []).join(', '),
    'Aesthetic: ' + (mb.visualAesthetic ?? []).join(', '),
    '',
    '## Colors',
    ...(mb.colors ?? []).map(c => `- ${c.hex} ${c.name} (${c.role})`),
    '',
    '## Typography',
    'Heading: ' + (mb.fonts?.heading?.family || '') + ' — ' + (mb.fonts?.heading?.style || ''),
    'Body: ' + (mb.fonts?.body?.family || '') + ' — ' + (mb.fonts?.body?.style || ''),
    '',
    '## Contact',
    'Phone: ' + (mb.facts?.phones ?? []).join(', '),
    'Email: ' + (mb.facts?.emails ?? []).join(', '),
    'Service area: ' + (mb.facts?.serviceArea || ''),
    '',
    '## Credentials',
    ...(mb.facts?.credentials ?? []).map(c => '- ' + c),
    '',
    '## Services',
    ...(mb.facts?.services ?? []).map(
      s =>
        `- ${s.name}${s.price ? ` ($${s.price})` : ''}${s.description ? `: ${s.description}` : ''}`,
    ),
    '',
    '## Target Audience',
    mb.targetAudience || '',
    '',
    '## Differentiator',
    mb.differentiator || '',
    '',
    '## Proof Points',
    ...(mb.proofPoints ?? []).map(p => '- ' + p),
  ];

  if (mb.facts?.testimonials?.length) {
    lines.push('', '## Testimonials');
    mb.facts.testimonials.forEach(t => {
      lines.push(`- "${t.quote}" — ${t.author}${t.role ? `, ${t.role}` : ''}`);
    });
  }

  return lines.filter(l => l !== '').join('\n');
};

// ── FACT MANAGEMENT ────────────────────────────────────────────────

/**
 * Add a fact value to a marketing brief facts category.
 * Mutates the facts object in place.
 */
export const addFact = (facts: MarketingBriefFacts, key: string, value: string): void => {
  if (!value?.trim()) {
    return;
  }
  const v = value.trim();

  if (key === 'serviceArea' || key === 'yearsInBusiness') {
    (facts as Record<string, unknown>)[key] = v;
  } else if (key === 'services') {
    if (!facts.services) {
      facts.services = [];
    }
    facts.services.push({ name: v, description: '', price: '' });
  } else if (key === 'namedStaff') {
    if (!facts.namedStaff) {
      facts.namedStaff = [];
    }
    facts.namedStaff.push({ name: v, role: '' });
  } else if (key === 'testimonials') {
    if (!facts.testimonials) {
      facts.testimonials = [];
    }
    facts.testimonials.push({ quote: v, author: '' });
  } else if (key === 'stats') {
    if (!facts.stats) {
      facts.stats = [];
    }
    facts.stats.push({ number: v, label: '' });
  } else {
    const arr = (facts as Record<string, unknown>)[key];
    if (!Array.isArray(arr)) {
      (facts as Record<string, unknown>)[key] = [];
    }
    ((facts as Record<string, unknown>)[key] as string[]).push(v);
  }
};

/**
 * Remove a fact value from a marketing brief facts category.
 * Mutates the facts object in place.
 */
export const removeFact = (facts: MarketingBriefFacts, key: string, idx: number): void => {
  if (key === 'serviceArea' || key === 'yearsInBusiness') {
    (facts as Record<string, unknown>)[key] = '';
  } else {
    const arr = (facts as Record<string, unknown>)[key];
    if (Array.isArray(arr)) {
      arr.splice(idx, 1);
    }
  }
};

// ── COLOR NORMALIZATION ────────────────────────────────────────────

interface ColorInput {
  hex?: string;
  value?: string;
  name?: string;
  role?: string;
}

interface NormalizedColor {
  hex: string;
  name: string;
  role: string;
}

/**
 * Normalize color array — AI may return different formats.
 */
export const normalizeColors = (colors: Array<string | ColorInput>): NormalizedColor[] => {
  if (!colors?.length) {
    return [];
  }
  return colors.map(c => {
    if (typeof c === 'string') {
      return { hex: c, name: c, role: '' };
    }
    return {
      hex: c.hex || c.value || '#ccc',
      name: c.name || c.hex || c.value || 'Color',
      role: c.role || '',
    };
  });
};
